import React, { useMemo, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import type { GetAppReviewStatsResponse } from "~/gen/rpc/app-review";
import useApi from "~/hooks/use-api";
import { Organization } from "../orgs";

function formatDateInputValue(iso: string): string {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildStatsUrl(params: {
  organizationUid?: string;
  sinceLastPromptedAt?: string;
}) {
  const url = new URL("/api/app-review/stats", "http://placeholder");

  if (params.organizationUid)
    url.searchParams.set("organizationUid", params.organizationUid);

  if (params.sinceLastPromptedAt)
    url.searchParams.set("sinceLastPromptedAt", params.sinceLastPromptedAt);

  return url.pathname + url.search;
}

function formatPercent(numerator: number, denominator: number): string {
  if (denominator === 0) return "0";

  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function makePieTooltipFormatter(total: number) {
  if (total === 0) return () => `0 (0%)`;

  return (value: number) => `${value} (${((value / total) * 100).toFixed(1)}%)`;
}

function StatCard({
  title,
  color,
  isLoading,
  stats,
}: {
  title: string;
  color: string;
  isLoading: boolean;
  stats: { label: string; value: string }[];
}) {
  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center mb-3">
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: color,
              marginRight: 8,
              flexShrink: 0,
            }}
          />
          <h5 className="mb-0">{title}</h5>
        </div>
        {isLoading ? (
          <Spinner size="sm" />
        ) : (
          stats.map(({ label, value }) => (
            <div key={label} className="mb-2">
              <small className="text-muted">{label}</small>
              <div className="h4">{value}</div>
            </div>
          ))
        )}
      </CardBody>
    </Card>
  );
}

export default function AppReviewsDashboard() {
  const [organizationUid, setOrganizationUid] = useState<string | undefined>(
    undefined
  );
  const [sinceLastPromptedAt, setSinceLastPromptedAt] = useState<
    string | undefined
  >(undefined);

  const { data: organizations } = useApi<Organization[]>(
    "/api/organization?includeDisabled=true"
  );

  const statsUrl = useMemo(
    () => buildStatsUrl({ organizationUid, sinceLastPromptedAt }),
    [organizationUid, sinceLastPromptedAt]
  );

  const {
    data: stats,
    isValidating: loading,
    error,
  } = useApi<GetAppReviewStatsResponse>(statsUrl, { revalidateOnFocus: false });

  const totalCount = stats?.totalCount ?? 0;
  const goodCount = stats?.goodCount ?? 0;
  const badCount = stats?.badCount ?? 0;
  const iosCount = stats?.iosCount ?? 0;
  const androidCount = stats?.androidCount ?? 0;
  const otherCount = stats?.otherCount ?? 0;
  const iosGoodCount = stats?.iosGoodCount ?? 0;
  const iosBadCount = stats?.iosBadCount ?? 0;
  const androidGoodCount = stats?.androidGoodCount ?? 0;
  const androidBadCount = stats?.androidBadCount ?? 0;

  const respondedCount = goodCount + badCount;

  return (
    <Container className="py-4">
      <h3 className="mb-4">App Feedback Report</h3>
      <p>
        {`These values come from our internal feedback system, not from the app
        store. Specifically, LOKE's app will sometimes prompt users if their app
        experience is Good 😊 or Bad 😕. This tracks users' responses (or lack
        thereof) to that prompt, not their actual submission to the Apple App
        Store or Google Play Store.`}
      </p>

      <Card className="mb-4">
        <CardBody>
          <Row>
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label>Organization</Label>
                <Input
                  type="select"
                  value={organizationUid ?? ""}
                  onChange={(e) =>
                    setOrganizationUid(e.target.value || undefined)
                  }
                >
                  <option value="">All organizations</option>
                  {(organizations ?? []).map((org) => (
                    <option key={org.uid} value={org.uid}>
                      {org.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label>Reviews Since</Label>
                <Input
                  type="date"
                  value={
                    sinceLastPromptedAt
                      ? formatDateInputValue(sinceLastPromptedAt)
                      : ""
                  }
                  onChange={(e) => {
                    if (!e.target.value) {
                      setSinceLastPromptedAt(undefined);
                      return;
                    }

                    // new Date("YYYY-MM-DD") parses as UTC midnight, shifting the
                    // cutoff by the user's UTC offset. The multi-argument constructor
                    // always uses local time, so this correctly represents local midnight.
                    const [year, month, day] = e.target.value
                      .split("-")
                      .map(Number);
                    setSinceLastPromptedAt(
                      new Date(year, month - 1, day, 0, 0, 0).toISOString()
                    );
                  }}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {error ? (
        <Alert color="danger">
          Error loading data: {error.message ?? "Unknown error"}
        </Alert>
      ) : (
        <>
          <Row>
            <Col md={4} className="mb-3">
              <StatCard
                title="Totals"
                color="#9ca3af"
                isLoading={loading}
                stats={[
                  {
                    label: "Users Prompted",
                    value: totalCount.toLocaleString(),
                  },
                  {
                    label: "Reviews Submitted",
                    value: respondedCount.toLocaleString(),
                  },
                  {
                    label: "Response Rate",
                    value: formatPercent(respondedCount, totalCount),
                  },
                ]}
              />
            </Col>
            <Col md={4} className="mb-3">
              <StatCard
                title="Positive Reviews"
                color="#22c55e"
                isLoading={loading}
                stats={[
                  {
                    label: "Positive Count",
                    value: goodCount.toLocaleString(),
                  },
                  {
                    label: "% of Total Prompts",
                    value: formatPercent(goodCount, totalCount),
                  },
                  {
                    label: "% of Responses",
                    value: formatPercent(goodCount, respondedCount),
                  },
                ]}
              />
            </Col>
            <Col md={4} className="mb-3">
              <StatCard
                title="Negative Reviews"
                color="#ef4444"
                isLoading={loading}
                stats={[
                  {
                    label: "Negative Count",
                    value: badCount.toLocaleString(),
                  },
                  {
                    label: "% of Total Prompts",
                    value: formatPercent(badCount, totalCount),
                  },
                  {
                    label: "% of Responses",
                    value: formatPercent(badCount, respondedCount),
                  },
                ]}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-3">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">All Reviews</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Positive", value: goodCount },
                            { name: "Negative", value: badCount },
                            {
                              name: "No Response",
                              value: Math.max(0, totalCount - respondedCount),
                            },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                        >
                          <Cell fill="#22c55e" />
                          <Cell fill="#ef4444" />
                          <Cell fill="#3b82f6" />
                        </Pie>
                        <Tooltip
                          formatter={makePieTooltipFormatter(totalCount)}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Reviews by Operating System</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "iOS", value: iosCount },
                            { name: "Android", value: androidCount },
                            { name: "Other", value: otherCount },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                        >
                          <Cell fill="#007AFF" />
                          <Cell fill="#3DDC84" />
                          <Cell fill="#9ca3af" />
                        </Pie>
                        <Tooltip
                          formatter={makePieTooltipFormatter(
                            iosCount + androidCount + otherCount
                          )}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md={4} className="mb-3">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Submitted Reviews</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Positive", value: goodCount },
                            { name: "Negative", value: badCount },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                        >
                          <Cell fill="#22c55e" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip
                          formatter={makePieTooltipFormatter(respondedCount)}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">iOS Reviews</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Positive", value: iosGoodCount },
                            { name: "Negative", value: iosBadCount },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                        >
                          <Cell fill="#22c55e" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip
                          formatter={makePieTooltipFormatter(
                            iosGoodCount + iosBadCount
                          )}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Android Reviews</h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Positive", value: androidGoodCount },
                            { name: "Negative", value: androidBadCount },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                        >
                          <Cell fill="#22c55e" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip
                          formatter={makePieTooltipFormatter(
                            androidGoodCount + androidBadCount
                          )}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
